import React, { useMemo } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import bytes from 'bytes';
import { defineMessages } from 'react-intl';

import Avatar from '@atlaskit/avatar';
import DynamicTable from '@atlaskit/dynamic-table';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/glyph/chevron-up';
import LockIcon from '@atlaskit/icon/glyph/lock';
import Pagination from '@atlaskit/pagination';
import { Box } from '@atlaskit/primitives';
import { RelativeDate } from '@atlassian/bitkit-date';

import RelativeLink from 'src/components/relative-link';
import RepositoryBuildStatus from 'src/components/repository-table/repository-build-status';
import { useAnalytics } from 'src/components/settings/analytics';
import NameCell from 'src/components/settings/name-cell';
import { useIntl } from 'src/hooks/intl';
import { link } from 'src/styles/mixins';

export type SortField = 'size' | 'name' | 'updated_on';

const messages = defineMessages({
  name: {
    id: 'frontbucket.projectRepositories.page.table.name',
    description: 'Repositories table column header for repository name',
    defaultMessage: 'Name',
  },
  size: {
    id: 'frontbucket.projectRepositories.page.table.size',
    description: 'Repositories table column header for repository size',
    defaultMessage: 'Size',
  },
  lastUpdated: {
    id: 'frontbucket.projectRepositories.page.table.lastUpdated',
    description:
      'Repositories table column header for repository last updated time stamp',
    defaultMessage: 'Last updated',
  },
  builds: {
    id: 'frontbucket.projectRepositories.page.table.builds',
    description: 'Repositories table column header for repository builds',
    defaultMessage: 'Builds',
  },
  privateRepository: {
    id: 'frontbucket.projectRepositories.page.table.privateRepository',
    description:
      'Label text for the lock icon that indicates the repository is private',
    defaultMessage: 'Private repository',
  },
});

const RepoLink = styled(RelativeLink)(link);

type RowCell = {
  key: string;
  content: React.ReactNode;
};

export type ProjectReposTableProps = {
  isLoading: boolean;
  page: number;
  pagelen: number;
  repositories: BB.Repository[];
  setPage: (pageNum: number) => void;
  size: number;
  handleSortChange: (field: SortField) => void;
  sortField: SortField;
  isAscending: boolean;
  isSizeSortEnabled: boolean;
};

export const ProjectRepositoriesTable = ({
  isLoading,
  size,
  repositories,
  page,
  pagelen,
  setPage,
  handleSortChange,
  sortField,
  isAscending,
  isSizeSortEnabled,
}: ProjectReposTableProps) => {
  const { formatMessage } = useIntl();
  const { publishLinkClickEvent } = useAnalytics();

  const tableHeader = useMemo(() => {
    const baseCells = [
      {
        key: 'name' as const,
        content: formatMessage(messages.name),
        isSortable: false,
        width: 50,
      },
      {
        key: 'updated_on' as const,
        content: (
          <span
            onClick={() => handleSortChange('updated_on')}
            style={{
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
            }}
            role="button"
            tabIndex={0}
            onKeyPress={e => {
              if (e.key === 'Enter' || e.key === ' ')
                handleSortChange('updated_on');
            }}
          >
            {formatMessage(messages.lastUpdated)}
            <Box
              paddingInlineStart="space.050"
              style={{ display: 'inline-block', opacity: 1 }}
            >
              {sortField === 'updated_on' ? (
                isAscending ? (
                  <ChevronUpIcon label="Ascending" />
                ) : (
                  <ChevronDownIcon label="Descending" />
                )
              ) : (
                <ChevronDownIcon label="Descending" />
              )}
            </Box>
          </span>
        ),
        isSortable: false,
        width: 21,
      },
      {
        key: 'builds' as const,
        content: formatMessage(messages.builds),
        isSortable: false,
        width: 11,
      },
      {
        key: 'is_private' as const,
        content: '',
        isSortable: false,
        width: 3,
      },
    ];

    const sizeCell = isSizeSortEnabled
      ? {
          key: 'size' as const,
          content: (
            <span
              onClick={() => handleSortChange('size')}
              style={{
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
              }}
              role="button"
              tabIndex={0}
              onKeyPress={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ')
                  handleSortChange('size');
              }}
            >
              {formatMessage(messages.size)}
              <Box
                paddingInlineStart="space.050"
                style={{ display: 'inline-block', opacity: 1 }}
              >
                {sortField === 'size' ? (
                  isAscending ? (
                    <ChevronUpIcon label="Ascending" />
                  ) : (
                    <ChevronDownIcon label="Descending" />
                  )
                ) : (
                  <ChevronDownIcon label="Descending" />
                )}
              </Box>
            </span>
          ),
          isSortable: false,
          width: 15,
        }
      : null;

    return {
      cells: [baseCells[0], sizeCell, ...baseCells.slice(1)].filter(
        Boolean
      ) as typeof baseCells,
    };
  }, [
    formatMessage,
    handleSortChange,
    isAscending,
    sortField,
    isSizeSortEnabled,
  ]);

  const tableRows = useMemo(() => {
    return repositories.map(repo => {
      const nameCell: RowCell = {
        key: `${repo.name}-name`,
        content: (
          <NameCell
            avatar={
              <Avatar
                src={repo?.links?.avatar?.href}
                size="small"
                appearance="square"
              />
            }
            name={
              repo.links?.html.href ? (
                <RepoLink
                  to={repo.links?.html.href}
                  onClick={() =>
                    publishLinkClickEvent('Repository', {
                      linkedRepositoryId: repo.uuid,
                    })
                  }
                >
                  {repo.name}
                </RepoLink>
              ) : (
                repo.name
              )
            }
          />
        ),
      };
      const sizeCell: RowCell | null = isSizeSortEnabled
        ? {
            key: `size-${repo.uuid}`,
            content:
              repo.size || repo.size === 0
                ? bytes(repo.size, {
                    unitSeparator: ' ',
                    decimalPlaces: 1,
                  })
                : '',
          }
        : null;
      const updatedOnCell: RowCell = {
        key: `last-updated-${repo.updated_on}`,
        content: repo.updated_on ? <RelativeDate date={repo.updated_on} /> : '',
      };
      const buildStatusCell: RowCell = {
        key: `build-status`,
        content: <RepositoryBuildStatus repository={repo} />,
      };
      const privateCell: RowCell = {
        key: `is-private`,
        content: repo.is_private ? (
          <LockIcon
            label={formatMessage(messages.privateRepository)}
            size="medium"
          />
        ) : (
          ''
        ),
      };

      // Use the defined RowCell type
      const cellsWithPotentialNull: (RowCell | null)[] = [
        nameCell,
        sizeCell,
        updatedOnCell,
        buildStatusCell,
        privateCell,
      ];

      // Filter out null using the correct type predicate
      const finalCells = cellsWithPotentialNull.filter(
        (cell): cell is RowCell => cell !== null
      );

      return {
        key: `row-${repo.uuid}`,
        cells: finalCells,
      };
    });
  }, [repositories, formatMessage, publishLinkClickEvent, isSizeSortEnabled]);

  return (
    <>
      <DynamicTable
        isLoading={isLoading}
        rowsPerPage={pagelen}
        page={page}
        head={tableHeader}
        rows={tableRows}
      />
      {size > pagelen && (
        <Box paddingBlockStart="space.200">
          <Pagination
            selectedIndex={page - 1}
            pages={Array.from(
              { length: Math.ceil(size / pagelen) },
              (_, i) => i + 1
            )}
            onChange={(_event, newPage: number) => setPage(newPage)}
          />
        </Box>
      )}
    </>
  );
};
